import React from 'react';

import Account from '@atlassian/account';
import {
	GlobalThemeProvider,
	useGlobalColorMode,
} from '@atlassian/shared-user-preferences-api/global-theme-provider';

import { useThemingState } from '@confluence/theming/entry-points/useThemingState';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder';
import { fg } from '@confluence/feature-gating';

import { getIdentityEnv } from './getIdentityEnv';
import type { User } from './CompanyHubTopNavigation';

type CompanyHubProfileMenuProps = {
	user: User;
};

const CompanyHubProfileMenuComponent = ({ user }: CompanyHubProfileMenuProps) => {
	const [themeState, themingActions] = useThemingState();
	const accountColorMode = useGlobalColorMode();
	const identityEnvironment = getIdentityEnv(user.environment);
	const primaryEmail = user?.emails?.find((email) => email?.isPrimary)?.value || '';
	const displayName = user?.displayName || '';
	const photo = user?.photos?.find((photo) => photo?.isPrimary)?.value || '';
	const renderServerPlaceholder = useRenderServerPlaceholder();
	// Only set continueUrl if we are not in SSR mode and we are in the browser to avoid breaking SSR
	const continueUrl =
		// eslint-disable-next-line no-restricted-syntax
		!renderServerPlaceholder && typeof window !== 'undefined' ? window.location.href : '';

	return (
		<Account
			UNSAFE_accountTheme={fg('shared-user-preferences') ? accountColorMode : undefined}
			testId="company-hub-profile-button"
			application="confluence"
			email={primaryEmail}
			identityEnvironment={identityEnvironment}
			continueUrl={continueUrl}
			name={displayName}
			picture={photo}
			currentTheme={themeState?.theme?.colorMode}
			onThemeChange={themingActions.setColorModePreference}
		/>
	);
};

export const CompanyHubProfileMenu = ({ user }: CompanyHubProfileMenuProps) => {
	return (
		<GlobalThemeProvider accountId={user.id}>
			<CompanyHubProfileMenuComponent user={user} />
		</GlobalThemeProvider>
	);
};
